import { useState, useEffect } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import client from '../api/client';

const LEVEL_STYLE: Record<string, any> = {
  저관여: { color: '#00D4AA', bg: '#0F2A22', label: '🟢 저관여' },
  중간관여: { color: '#F6AD55', bg: '#2A1F0D', label: '🟡 중간관여' },
  고관여: { color: '#F56565', bg: '#2A0D0D', label: '🔴 고관여' },
};

const WEEK_LABEL = ['1주차', '2주차', '3주차', '4주차'];

export default function Report() {
  const checkAuth = useAuthGuard();
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    client.get('/report/list').then(r => setStrategies(r.data.strategies)).catch(() => {});
  }, []);

  const handleGenerate = async (id: number) => {
    setLoading(true);
    try {
      const res = await client.get(`/report/generate/${id}`);
      setReport(res.data);
      setSelected(strategies.find(s => s.id === id));
    } catch { alert('리포트 생성 실패'); }
    finally { setLoading(false); }
  };

  const printStyle = `
@media print {
  nav, aside, header, .no-print { display: none !important; }
  body { background: white !important; color: black !important; }
  * { color: black !important; background: white !important; border-color: #ccc !important; }
  a { text-decoration: none; }
}
`;

  const handlePrint = () => {
    if (!checkAuth()) return;
    window.print();
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 16 },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 28px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '7px 16px', color: '#8892A4', fontSize: 12, cursor: 'pointer' },
    btnBlue: { background: '#1A3A5C', border: 'none', borderRadius: 8, padding: '10px 24px', color: '#378ADD', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    section: { marginBottom: 24 },
    sectionTitle: { color: '#E8ECF4', fontSize: 15, fontWeight: 700, marginBottom: 12, paddingBottom: 8, borderBottom: '0.5px solid #2A3045' },
  };

  const lv = report ? LEVEL_STYLE[report.level] : null;

  return (
    <Layout>
      <style>{printStyle}</style>
      <div style={{ maxWidth: 900 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#E8ECF4', fontSize: 18, fontWeight: 700 }}>Step 4. 전략 리포트</div>
          <div style={{ color: '#8892A4', fontSize: 12, marginTop: 4 }}>Step 1~3 결과를 종합한 마케팅 플랜입니다</div>
        </div>

        {/* 전략 선택 */}
        {!report && (
          <>
            {strategies.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', padding: 40 }}>
                <div style={{ color: '#F6AD55', fontSize: 14, marginBottom: 8 }}>⚠️ 먼저 Step 3. 키워드 전략을 완료해주세요</div>
                <button style={s.btn} onClick={() => navigate('/marketing')}>마케팅 전략 하러가기</button>
              </div>
            ) : (
              <>
                <div style={{ color: '#B0BAC8', fontSize: 13, marginBottom: 12 }}>리포트를 생성할 마케팅 전략을 선택하세요</div>
                {strategies.map(st => {
                  const lv = LEVEL_STYLE[st.level];
                  return (
                    <div key={st.id} style={s.card}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{st.title}</div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ background: lv?.color + '22', border: `1px solid ${lv?.color}55`, borderRadius: 6, padding: '2px 10px', color: lv?.color, fontSize: 12, fontWeight: 600 }}>{lv?.label}</span>
                            {st.channels && <span style={{ color: '#8892A4', fontSize: 12 }}>{st.channels}</span>}
                            <span style={{ color: '#4A5568', fontSize: 11 }}>{new Date(st.created_at).toLocaleDateString('ko-KR')}</span>
                          </div>
                        </div>
                        <button style={s.btn} onClick={() => handleGenerate(st.id)} disabled={loading}>
                          {loading ? '생성 중...' : '리포트 생성'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}

        {/* 리포트 본문 */}
        {report && lv && (
          <>
            {/* 헤더 */}
            <div style={{ ...s.card, background: lv.bg, border: `1px solid ${lv.color}33`, marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ color: '#8892A4', fontSize: 11, marginBottom: 4 }}>전략 리포트</div>
                  <div style={{ color: '#E8ECF4', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{report.title}</div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ background: lv.color + '22', border: `1px solid ${lv.color}`, borderRadius: 6, padding: '3px 12px', color: lv.color, fontSize: 13, fontWeight: 700 }}>{lv.label}</span>
                    <span style={{ color: '#8892A4', fontSize: 13 }}>관여도 평균 {report.avg_score}점</span>
                    {report.channels && <span style={{ color: '#8892A4', fontSize: 13 }}>{report.channels}</span>}
                  </div>
                  <div style={{ color: lv.color, fontSize: 13, marginTop: 10, fontStyle: 'italic' }}>{report.consumer_mind}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={s.btnGhost} onClick={handlePrint}>🖨️ PDF 저장</button>
                  <button style={s.btnGhost} onClick={() => { setReport(null); setSelected(null); }}>다시 선택</button>
                </div>
              </div>
            </div>

            {/* 핵심 키워드 TOP 5 */}
            {report.top_keywords?.length > 0 && (
              <div style={s.section}>
                <div style={s.sectionTitle}>🔑 핵심 키워드 TOP 5</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
                  {report.top_keywords.map((kw: any, i: number) => {
                    const scoreColor = kw.score >= 8 ? '#00D4AA' : kw.score >= 5 ? '#F6AD55' : '#F56565';
                    return (
                      <div key={i} style={{ background: '#161B27', border: `0.5px solid ${scoreColor}44`, borderRadius: 8, padding: '14px 10px', textAlign: 'center' }}>
                        <div style={{ color: scoreColor, fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{kw.score}</div>
                        <div style={{ color: '#E8ECF4', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{kw.keyword}</div>
                        <div style={{ color: '#8892A4', fontSize: 11 }}>{kw.monthly_search?.toLocaleString()}회</div>
                      </div>
                    );
                  })}
                </div>
                {report.top_keywords.length === 0 && (
                  <div style={{ color: '#4A5568', fontSize: 13 }}>Step 3 키워드 전략 완료 후 확인 가능합니다</div>
                )}
              </div>
            )}

            {/* 채널별 전략 */}
            {report.channel_tips?.length > 0 && (
              <div style={s.section}>
                <div style={s.sectionTitle}>📢 채널별 전략</div>
                {report.channel_tips.map((tip: string, i: number) => (
                  <div key={i} style={{ background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 6, padding: '10px 14px', marginBottom: 8, color: '#E8ECF4', fontSize: 13, lineHeight: 1.6 }}>{tip}</div>
                ))}
              </div>
            )}

            {/* 바이럴 전략 */}
            {report.viral_strategy && (
              <div style={s.section}>
                <div style={s.sectionTitle}>{report.viral_strategy.title}</div>
                {report.viral_strategy.items.map((item: string, i: number) => (
                  <div key={i} style={{ display: 'flex', gap: 10, background: '#1A0D0D', border: '0.5px solid #F5656533', borderRadius: 6, padding: '10px 14px', marginBottom: 8, alignItems: 'flex-start' }}>
                    <span style={{ color: '#F56565', flexShrink: 0 }}>🔥</span>
                    <span style={{ color: '#E8ECF4', fontSize: 13, lineHeight: 1.6 }}>{item}</span>
                  </div>
                ))}
              </div>
            )}

            {/* 광고 예산 */}
            <div style={s.section}>
              <div style={s.sectionTitle}>💰 광고 예산 배분</div>
              <div style={{ background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 6, padding: '14px', color: '#E8ECF4', fontSize: 13, lineHeight: 1.6 }}>{report.budget_plan}</div>
            </div>

            {/* 즉시 실행 체크리스트 */}
            <div style={s.section}>
              <div style={s.sectionTitle}>✅ 즉시 실행 체크리스트</div>
              <div style={{ background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: '4px 0' }}>
                {report.checklist?.map((item: string, i: number) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 16px', borderBottom: i < report.checklist.length - 1 ? '0.5px solid #1E2535' : 'none', alignItems: 'flex-start' }}>
                    <span style={{ color: '#00D4AA', fontSize: 14, flexShrink: 0 }}>☐</span>
                    <span style={{ color: '#E8ECF4', fontSize: 13, lineHeight: 1.6 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 4주 액션 플랜 */}
            <div style={s.section}>
              <div style={s.sectionTitle}>📅 이번 달 4주 액션 플랜</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                {WEEK_LABEL.map((week, wi) => {
                  const weekKey = `week${wi + 1}`;
                  const tasks = report.action_plan?.[weekKey] || [];
                  return (
                    <div key={week} style={{ background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 16 }}>
                      <div style={{ color: lv.color, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{week}</div>
                      {tasks.map((task: string, ti: number) => (
                        <div key={ti} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                          <span style={{ color: '#4A5568', fontSize: 12, flexShrink: 0, marginTop: 1 }}>{ti + 1}.</span>
                          <span style={{ color: '#B0BAC8', fontSize: 13, lineHeight: 1.5 }}>{task}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 마케팅 문의 */}
            <div style={{ background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>전문 마케팅 대행이 필요하신가요?</div>
                <div style={{ color: '#8892A4', fontSize: 12, marginTop: 4 }}>바이럴·광고·콘텐츠 마케팅 전문 상담 — 이 리포트를 가지고 문의하세요</div>
              </div>
              <button onClick={() => alert('카카오톡 오픈채팅 URL을 설정해주세요')}
                style={{ background: '#FEE500', border: 'none', borderRadius: 8, padding: '12px 24px', color: '#000', fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0, marginLeft: 16 }}>
                💬 마케팅 문의하기
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
